import React, { useCallback, useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import "../../App.css";
import TypingDots from "../../animations/TypingDots";
import { ResizableBox, ResizeCallbackData } from "react-resizable";
import { Coordinate } from "ol/coordinate";
import { RiverSensor } from "../../riverSensors/RiverSensors";
import EWSRiverChart from "./EWSRiverChart";
import { EWSSidebarMap, openEwsWindowsMap } from "./ShowEarlyWarning";
import { map } from "../MainLayer";
import Overlay from "ol/Overlay";

interface Props {
  warningId: string;
  coordinates: Coordinate | null;
  adm4_en: string | null;
  active_pcode: string | null;
  end_time: string | null;
  s_desc: string | null;
  severity_index: string | null;
  severity: string | null;
  adm4_pcode: string | null;
  adm3_pcode: string | null;
  adm3_en: string | null;
  adm2_pcode: string | null;
  adm2_en: string | null;
  active_alevel: string | null;
  en_desc: string | null;
  validityPeriod: string | null;
  issued_time: string | null;
  w_status: string | null;
  w_desc: string | null;
  w_name: string | null;
  w_type: string | null;
  adm1_en: string | null;
  onClose: (incidentId: string) => void;
  addItem?: (id: string, type: "ew" | "river" | "air" | "hz" | "rain") => void;
  id?: string;
}

const EarlyWarningPopup: React.FC<Props> = ({
  warningId,
  coordinates,
  adm4_en,
  active_pcode,
  end_time,
  s_desc,
  severity_index,
  severity,
  adm4_pcode,
  adm3_pcode,
  adm3_en,
  adm2_pcode,
  adm2_en,
  active_alevel,
  en_desc,
  validityPeriod,
  issued_time,
  w_status,
  w_desc,
  w_name,
  w_type,
  adm1_en,
  onClose,
  addItem,
  id,
}) => {
  const [showDialog, setShowDialog] = useState<boolean>(true);
  const [activeFullScreen, setActiveFullScreen] = useState<boolean>(false);
  const fullscreenModal = useRef<HTMLDivElement | null>(null);
  const [riverSensors, setRiverSensors] = useState<RiverSensor[]>([]);
  const [showCharts, setShowCharts] = useState(false);
  const [showRainfallCharts, setShowRainfallCharts] = useState(false);
  const [dimensions, setDimensions] = useState({
    resizableBoxwidth: 0,
    resizableBoxheight: 0,
  });
  // const { EWSOpenSidebarMap,removeFromEWSOpenSidebarMap } = useEWSContext();
  const [dimensionsMin, setDimensionsMin] = useState({
    resizableBoxMinwidth: 0,
    resizableBoxMinheight: 0,
  });
  //----------------resibleBox Function----------------
  const updateDimensions = () => {
    const vw = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    );
    const vh = Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0
    );
    if (vw > 2000 && vh > 1200) {
      setDimensions({
        resizableBoxwidth: vw * 0.3,
        resizableBoxheight: vh * 0.3,
      });
    } else {
      setDimensions({
        resizableBoxwidth: vw * 0.3,
        resizableBoxheight: vh * 0.6,
      });
    }

    if (vw > 2000 && vh > 1200) {
      setDimensionsMin({
        resizableBoxMinwidth: vw * 0.2,
        resizableBoxMinheight: vh * 0.3,
      });
    } else {
      setDimensionsMin({
        resizableBoxMinwidth: vw * 0.3,
        resizableBoxMinheight: vh * 0.4,
      });
    }
  };

  useEffect(() => {
    updateDimensions();

    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const openContentFullscreen = () => {
    if (!document.fullscreenElement) {
      enterFullscreen();
    } else {
      exitFullScreen();
    }
  };

  const enterFullscreen = () => {
    const elem = fullscreenModal.current;
    if (elem) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      }
      setActiveFullScreen(true);
    }
  };

  const exitFullScreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if ((document as any).mozCancelFullScreen) {
      (document as any).mozCancelFullScreen();
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      (document as any).msExitFullscreen();
    }
    setActiveFullScreen(false);
  };

  const [widthMain, setWidthMain] = useState<number>(0);
  const [heightMain, setHeightMain] = useState<number>(0);

  useEffect(() => {}, [widthMain, heightMain]);

  const handleResize = (
    event: React.SyntheticEvent,
    data: ResizeCallbackData
  ) => {
    setWidthMain(data.size.width);
    setHeightMain(data.size.height);
  };

  const riverChartsShow = () => {
    setShowCharts((current) => !current);
    fetchData();
  };

  const rainfallChartsShow = () => {
    setShowRainfallCharts((current) => !current);
  };

  const RIVER_LEVEL_URL =
  process.env.REACT_APP_RIVER_LEVEL_URL;
  const RIVER_LEVEL_DATA_URL =
  process.env.REACT_APP_RIVER_LEVEL_DATA_URL;

  const fetchData = async () => {
    try {
      const response = await fetch(
       `${RIVER_LEVEL_URL}`
      );
      const responseData = await response.json();
      if (Array.isArray(responseData)) {
        const data: RiverSensor[] = responseData;
        setRiverSensors(data);
      } else {
        console.error("Invalid response data format:", responseData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchSensorCurrentValue = async (
    id: string
  ): Promise<number | null> => {
    try {
      const response = await fetch(
        `${RIVER_LEVEL_DATA_URL}${id}`
      );
      const responseData = await response.json();

      if (responseData && typeof responseData.level === "number") {
        return responseData.level;
      } else {
        console.error(
          "Invalid response data format or missing level:",
          responseData
        );
        return null;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      return null;
    }
  };

  const [counter, setCounter] = useState(0);

  const closePopup = (active_pcode: string) => {
    setShowDialog(false);
    if (EWSSidebarMap.has(active_pcode)) {
      EWSSidebarMap.delete(active_pcode);
    }
    if (active_pcode) {
      onClose(active_pcode);
      openEwsWindowsMap.set(active_pcode, "close");
      // removeFromEWSOpenSidebarMap(active_pcode);
    }
    removeOverlay(active_pcode);
    const entries = Array.from(EWSSidebarMap.values());
    const lastEntryIndex = entries.length - 1;
    entries.forEach((details, index) => {
      const { coordinates, active_pcode } = details;
      if (coordinates) {
        removeOverlay(active_pcode!);
        if (index === lastEntryIndex) {
          // Handle last entry
          addOverlayClickItemLast(active_pcode!, coordinates);
        } else {
          // Handle all other entries
          addOverlayClickItem(active_pcode!, coordinates);
        }
      }
    });
  };

  const removeOverlay = (key: string) => {
    const overlays = map.getOverlays().getArray();
    const overlaysToRemove = overlays.filter(
      (overlay) => overlay.getId() === key
    );
    overlaysToRemove.forEach((overlay) => {
      map.removeOverlay(overlay);
    });
  };

  const addOverlayClickItem = (id: string, coordinates: Coordinate) => {
    const markerElement = document.createElement("div");
    markerElement.className = "location-dot animate";

    const ringElement = document.createElement("div");
    ringElement.className = "location-dot-ring";

    markerElement.appendChild(ringElement);
    const overlay = new Overlay({
      id: id,
      position: coordinates,
      positioning: "center-center",
      element: markerElement,
      stopEvent: false,
    });
    map.addOverlay(overlay);
  };

  const addOverlayClickItemLast = (id: string, coordinates: Coordinate) => {
    const markerElement = document.createElement("div");
    markerElement.className = "locationlast-dot animate";
    const ringElement = document.createElement("div");
    ringElement.className = "locationlast-dot-ring";

    markerElement.appendChild(ringElement);
    const overlay = new Overlay({
      id: id,
      position: coordinates,
      positioning: "center-center",
      element: markerElement,
      stopEvent: false,
    });
    map.addOverlay(overlay);
  };

  const iconEarlyWarningMap = new Map([
    [
      "3",
      new Map([
        ["LSW", "assets/el.png"],
        ["HRW", "assets/eh.png"],
        ["FLW", "assets/ef.png"],
        ["TSM", "assets/tsunami_icon.png"],
        ["HWN", "assets/ew.png"],
        ["SAN", "assets/em.png"],
        ["LIG", "assets/elh.png"],
      ]),
    ],
    [
      "1",
      new Map([
        ["LSW", "assets/wl.png"],
        ["HRW", "assets/wh.png"],
        ["FLW", "assets/wf.png"],
        ["TSM", "assets/tsunami_icon.png"],
        ["HWN", "assets/ww.png"],
        ["SAN", "assets/wm.png"],
        ["LIG", "assets/wlh.png"],
      ]),
    ],
    [
      "2",

      new Map([
        ["LSW", "assets/al.png"],
        ["HRW", "assets/ah.png"],
        ["FLW", "assets/af.png"],
        ["TSM", "assets/tsunami_icon.png"],
        ["HWN", "assets/aw.png"],
        ["SAN", "assets/am.png"],
        ["LIG", "assets/alh.png"],
      ]),
    ],
  ]);

  const colorContainer = new Map([
    ["3", "red"],
    ["1", "rgb(235, 235, 2)"],
    ["2", "rgb(255, 185, 54)"],
  ]);

  const getIconPath = (
    severity_index: string | null,
    w_type: string | null
  ): string | undefined => {
    if (severity_index && w_type) {
      const severityMap = iconEarlyWarningMap.get(severity_index);
      if (severityMap) {
        return severityMap.get(w_type);
      }
    }
    return undefined; // Return undefined if the path is not found
  };

  const iconPath = getIconPath(severity_index, w_type);

  const [selectedTab, setSelectedTab] = useState<
    "early_warning_data_tab" | "realtime_data_tab" | "forecast_tab"
  >("early_warning_data_tab");
  // Forecast selected model
  const [selectedForecastModel, setSelectedForecastModel] = useState<
    "forecast_model_ecmwf" | "forecast_model_gfs" | "forecast_model_icon"
  >("forecast_model_ecmwf");

  // Nearest river gauges status
  const [isNearestRiverGaugeOpen, setIsNearestRiverGaugeOpen] =
    useState<boolean>(true);
  // Nearest rainfall gauges status
  const [isNearestRainfallGaugeOpen, setIsNearestRainfallGaugeOpen] =
    useState<boolean>(false);
  // Nearest early warning status
  const [isNearestReportedIncidentOpen, setIsNearestReportedIncidentOpen] =
    useState<boolean>(false);

  const popupRef = useRef<HTMLDivElement | null>(null);
  const enteredRef = useRef<boolean>(false);

  const mouseMoveEvent = useCallback((event: MouseEvent) => {
    const sideMenu = document.getElementById("side-menu-wrapper");

    if (sideMenu && !enteredRef.current) {
      const sideMenuWidth = sideMenu.getBoundingClientRect();
      const sideMenuLeft = sideMenuWidth.left;

      if (sideMenuLeft < event.clientX) {
        enteredRef.current = true;
        if (id && addItem) {
          addItem(id, "ew");
          setShowDialog(false);
        }
      }
    }
  }, []);

  const touchMoveEvent = useCallback((event: TouchEvent) => {
    const sideMenu = document.getElementById("side-menu-wrapper");

    if (sideMenu && !enteredRef.current) {
      const sideMenuWidth = sideMenu.getBoundingClientRect();
      const sideMenuLeft = sideMenuWidth.left;
      const touchX = event.touches[0].clientX; 

      if (sideMenuLeft < touchX) {
        enteredRef.current = true;
        if (id && addItem) {
          addItem(id, "ew");
          setShowDialog(false);
        }
      }
    }
  }, []);

  useEffect(() => {
    const handleMouseDown = () => {
      document.addEventListener("mousemove", mouseMoveEvent);
      document.addEventListener("mouseup", handleMouseUp);
    };
  
    const handleMouseUp = () => {
      document.removeEventListener("mousemove", mouseMoveEvent);
    };
  
    const handleTouchStart = () => {
      document.addEventListener("touchmove", touchMoveEvent);
      document.addEventListener("touchend", handleTouchEnd);
    };
  
    const handleTouchEnd = () => {
      document.removeEventListener("touchmove", touchMoveEvent);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  
    if (popupRef.current) {
      popupRef.current.addEventListener("mouseenter", () => {
        popupRef.current?.addEventListener("mousedown", handleMouseDown);
        popupRef.current?.addEventListener("touchstart", handleTouchStart);
      });
    }
  
    return () => {
      document.removeEventListener("mousemove", mouseMoveEvent);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", touchMoveEvent);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [mouseMoveEvent, touchMoveEvent]);

  return (
    <div
      className="ol-overlay-container ol-selectable"
      style={{
        width: "10px",
        height: "10px",
      }}
    >
      <div
        style={{ visibility: showDialog ? "visible" : "hidden", width: "0%" }}
      >
        <Draggable
          handle=".drag-handle"
          cancel=".no-drag"
          enableUserSelectHack={false}
        >
          <ResizableBox
            className="custom-resizable-box"
            width={dimensions.resizableBoxwidth}
            height={dimensions.resizableBoxheight}
            minConstraints={[
              dimensionsMin.resizableBoxMinwidth,
              dimensionsMin.resizableBoxMinheight,
            ]}
            onResize={handleResize}
            /* maxConstraints={[800, 600]} */
            resizeHandles={["s", "se", "sw", "ne", "nw"]}
            style={{ zIndex: 1, left: "-30vw", right: "0vw", top: "-40vh" }}
          >
            <div
              className="drag-handle"
              ref={fullscreenModal}
              style={{
                width: "100%",
                height: "100%",
                position: "absolute",
                cursor: "move",
                touchAction: "none",
              }}
            >
              <div className={`earlyWarningPopupContainer`}>
                {/* Title container */}
                <div className={`titleContainer`} ref={popupRef}>
                  <span>Early Warning</span>
                  <div className={`titleButtonContainer`}>
                    <i
                      className={`titleIcon fullScreenIco`}
                      onClick={openContentFullscreen}
                      onTouchStart={openContentFullscreen}
                    ></i>
                    <i
                      className={`titleIcon closeIco`}
                      onClick={() => closePopup(active_pcode!)}
                      onTouchEnd={() => closePopup(active_pcode!)}
                    ></i>
                  </div>
                </div>
                {/* Tab area */}
                <div className={`tabArea`}>
                  <div
                    className={`tabButton ${
                      selectedTab === "early_warning_data_tab"
                        ? "tabSelected"
                        : ""
                    }`}
                    id="early_warning_data_tab"
                    onClick={(e) =>
                      setSelectedTab((prevState) => "early_warning_data_tab")
                    }
                    onTouchStart={(e) =>
                      setSelectedTab((prevState) => "early_warning_data_tab")
                    }
                  >
                    early warnings
                  </div>
                  <div
                    className={`tabButton ${
                      selectedTab === "realtime_data_tab" ? "tabSelected" : ""
                    }`}
                    id="realtime_data_tab"
                    onClick={(e) =>
                      setSelectedTab((prevState) => "realtime_data_tab")
                    }
                    onTouchStart={(e) =>
                      setSelectedTab((prevState) => "realtime_data_tab")
                    }
                  >
                    real-time data
                  </div>
                  <div
                    className={`tabButton ${
                      selectedTab === "forecast_tab" ? "tabSelected" : ""
                    }`}
                    id="forecast_tab" /* onClick={(e) => setSelectedTab(prevState => 'forecast_tab')} */
                  >
                    forecast
                  </div>
                </div>

                {/* Early warning data container */}
                {selectedTab === "early_warning_data_tab" && (
                  <div className={`tabEarlyWarningDataContainer`}>
                    <div className={`detailsContainer`}>
                      <div
                        className={`iconContainer`}
                        style={{
                          backgroundColor: `${colorContainer.get(
                            severity_index || "default"
                          )}`,
                        }}
                      >
                        <span>
                          {" "}
                          {w_name} - {adm3_en}
                        </span>
                      </div>
                      <div className={`dataRow`}>
                        <span className={`name`}>Description</span>
                        <span className={`data`}>
                          {en_desc} {<TypingDots />}
                        </span>
                      </div>
                      <div className={`dataRow`}>
                        <span className={`name`}>Division</span>
                        <span className={`data`}>{adm3_en}</span>
                      </div>
                      <div className={`dataRow`}>
                        <span className={`name`}>District</span>
                        <span className={`data`}>{adm2_en}</span>
                      </div>
                      <div className={`dataRow`}>
                        <span className={`name`}>Province</span>
                        <span className={`data`}>{adm1_en}</span>
                      </div>
                      <div className={`dataRow`}>
                        <span className={`name`}>Severity</span>
                        <span className={`data`}>{severity}</span>
                      </div>
                      <div className={`dataRow`}>
                        <span className={`name`}>Start Date</span>
                        <span className={`data`}>{issued_time}</span>
                      </div>

                      <div className={`dataRow`}>
                        <span className={`name`}>End Date</span>
                        <span className={`data`}>{validityPeriod}</span>
                      </div>
                      <div className={`dataRow`}>
                        <span className={`name`}>Status</span>
                        <span className={`data`}>{w_status}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Real-time tab data container */}
                {selectedTab === "realtime_data_tab" && (
                  <div className={`tabRealtimeDataContainer`}>
                    {/* Nearest river gauges */}

                    <div className={`mapContainer`}>
                      <div
                        className={`titleContainer`}
                        style={{
                          borderRadius: `${
                            isNearestRiverGaugeOpen
                              ? "0.5rem 0.5rem 0 0"
                              : "0.5rem"
                          }`,
                        }}
                        onClick={(e) =>
                          setIsNearestRiverGaugeOpen((prevState) => !prevState)
                        }
                      >
                        <span>nearest river gauges</span>
                        <i
                          className={`${
                            isNearestRiverGaugeOpen ? "up" : "down"
                          }`}
                        ></i>
                      </div>
                      {isNearestRiverGaugeOpen && (
                        <div className={`mapDetailsContainer`}>
                          {/* Nearest river gauge map here */}

                          <div
                            className="no-drag"
                            style={{
                              height: "100%",
                              width: "100%",
                              display: "flex",
                              alignItems: "start",
                              margin: "15px",
                              flexDirection: "column",
                              justifyContent: "top",
                            }}
                          >
                            <EWSRiverChart
                              id="Nawalapitiya"
                              name="Mahaweli Ganga(Nawalapitiya)"
                              critical={2}
                              cautious={1}
                              warning={0.75}
                              fullScreen={false}
                              showDialog={false}
                              chartWidth={widthMain * 0.8}
                              chartHeight={heightMain * 0.6}
                            />
                          </div>
                          <div
                            className="no-drag"
                            style={{
                              height: "100%",
                              width: "100%",
                              display: "flex",
                              alignItems: "start",
                              margin: "15px",
                              flexDirection: "column",
                              justifyContent: "top",
                            }}
                          >
                            <EWSRiverChart
                              id="Kalawellawa (Millakanda)"
                              name="Kalu Ganga(Kalawellawa - Millakanda)"
                              critical={2}
                              cautious={1}
                              warning={0.75}
                              fullScreen={false}
                              showDialog={false}
                              chartWidth={widthMain * 0.8}
                              chartHeight={heightMain * 0.6}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Nearest rainfall gauges */}

                    {/* <div className={`mapContainer`}>
                      <div
                        className={`titleContainer`}
                        style={{
                          borderRadius: `${
                            isNearestRainfallGaugeOpen
                              ? "0.5rem 0.5rem 0 0"
                              : "0.5rem"
                          }`,
                        }}
                        onClick={(e) =>
                          setIsNearestRainfallGaugeOpen(
                            (prevState) => !prevState
                          )
                        }
                      >
                        <span>nearest rainfall gauges</span>
                        <i
                          className={`${
                            isNearestRainfallGaugeOpen ? "up" : "down"
                          }`}
                        ></i>
                      </div>
                      {isNearestRainfallGaugeOpen && (
                        <div className={`mapDetailsContainer`}>
                          <span>Nearest rainfall gauge map here</span>
                        </div>
                      )}
                    </div> */}

                    {/* Nearest reported incident */}

                    {/* <div className={`mapContainer`}>
                      <div
                        className={`titleContainer`}
                        style={{
                          borderRadius: `${
                            isNearestReportedIncidentOpen
                              ? "0.5rem 0.5rem 0 0"
                              : "0.5rem"
                          }`,
                        }}
                        onClick={(e) =>
                          setIsNearestReportedIncidentOpen(
                            (prevState) => !prevState
                          )
                        }
                      >
                        <span>nearest reported incidents</span>
                        <i
                          className={`${
                            isNearestReportedIncidentOpen ? "up" : "down"
                          }`}
                        ></i>
                      </div>
                      {isNearestReportedIncidentOpen && (
                        <div className={`mapDetailsContainer`}>
                          <span> Nearest reported incident map here</span>
                        </div>
                      )}
                    </div> */}
                  </div>
                )}

                {/*forecast data container */}
                {selectedTab === "forecast_tab" && (
                  <div className={`$tabForecastContainer`}>
                    {/* Forecast model selector */}
                    <div className={`$modelSelectorContainer`}>
                      <span
                        className={`${
                          selectedForecastModel === "forecast_model_ecmwf"
                            ? "modelSelected"
                            : ""
                        }`}
                        id="forecast_model_ecm"
                        onClick={(e) =>
                          setSelectedForecastModel(
                            (prevState) => "forecast_model_ecmwf"
                          )
                        }
                      >
                        ecmwf
                      </span>
                      <span
                        className={`${
                          selectedForecastModel === "forecast_model_gfs"
                            ? "modelSelected"
                            : ""
                        }`}
                        id="forecast_model_gfs"
                        onClick={(e) =>
                          setSelectedForecastModel(
                            (prevState) => "forecast_model_gfs"
                          )
                        }
                      >
                        gfs
                      </span>
                      <span
                        className={`${
                          selectedForecastModel === "forecast_model_icon"
                            ? "modelSelected"
                            : ""
                        }`}
                        id="forecast_model_icon"
                        onClick={(e) =>
                          setSelectedForecastModel(
                            (prevState) => "forecast_model_icon"
                          )
                        }
                      >
                        icon
                      </span>
                    </div>
                    {/* Forecast data container */}
                    {/* ECMWF */}
                    {selectedForecastModel === "forecast_model_ecmwf" && (
                      <div className={`forecastDataContainer`}>
                        <div className={`gridCell chart`}>
                          {/* ECM map here */}
                          <span>ECM Map</span>
                        </div>
                        <div className={`gridCell color`}>
                          <span>ECM Map Colors</span>
                        </div>
                        <div className={`gridCell details`}>
                          <span>ECM Map details</span>
                        </div>
                      </div>
                    )}

                    {/* GFS */}
                    {selectedForecastModel === "forecast_model_gfs" && (
                      <div className={`forecastDataContainer`}>
                        <div className={`gridCell chart`}>
                          {/* GFS map here */}
                          <span>GFS Map</span>
                        </div>
                        <div className={`gridCell color`}>
                          <span>GFS Map Colors</span>
                        </div>
                        <div className={`gridCell details`}>
                          <span>GFS Map details</span>
                        </div>
                      </div>
                    )}

                    {/* ICON */}
                    {selectedForecastModel === "forecast_model_icon" && (
                      <div className={`forecastDataContainer`}>
                        <div className={`gridCell chart`}>
                          {/* ICON map here */}
                          <span>ICON Map</span>
                        </div>
                        <div className={`gridCell color`}>
                          <span>ICON Map Colors</span>
                        </div>
                        <div className={`gridCell details`}>
                          <span>ICON Map details</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </ResizableBox>
        </Draggable>
      </div>
    </div>
  );
};

export default EarlyWarningPopup;
